import { useState, useEffect } from "react";
import { Star, Sparkles, Send, RefreshCw, Loader2 } from "lucide-react";

export default function Reviews() {
    const [reviewList, setReviewList] = useState(() => {
        if (typeof window !== "undefined") {
            const savedReviews = localStorage.getItem("gb_testimonials");
            if (savedReviews) return JSON.parse(savedReviews);
        }
        return [
            {
                name: "Henry Oyenola",
                role: "Devops Engineer",
                rating: 4,
                text: `I recently secured a job through Genius Bridge Technologies, and my experience with the team has been excellent. The entire staff is highly professional, and Badal has been incredibly supportive throughout the process. Every team member handled my profile with genuine care and efficiency, ensuring a smooth and successful placement experience.\n\nI truly appreciate their dedication, and I genuinely recommend Genius Bridge Technologies to anyone looking for reliable job placement support.`,
            },
            {
                name: "Michael Adewale",
                role: "Project Manager",
                rating: 5,
                text: "I had the absolute pleasure of working with Badal at Genius Bridge Technologies, and I can confidently commend his support and resources. I highly recommend Badal to anyone seeking to elevate their career with expert marketing and branding support.",
            },
            {
                name: "Liam Smith",
                role: "Software Engineer",
                rating: 5,
                text: `I had the pleasure of working with Genius Bridge Technologies during my job search, and I can confidently say that its support made a significant difference in my journey. Genius Bridge Technologies brings a rare combination of deep industry knowledge, strategic insight, and genuine care for its clients.\n\nthey took the time to understand my background, strengths, and career goals, and quickly matched me with opportunities that aligned perfectly with my skill set. His communication was always prompt, transparent, and encouraging, helping me stay confident and focused throughout the process.\n\nThanks to Genius Bridge Technologies' guidance and network, I was able to land a role that I’m truly excited about. I highly recommend him to anyone looking to take the next step in their career. they are not just a recruiter, they’re a true partner in your success.`,
            }
        ];
    });

    const [selectedRating, setSelectedRating] = useState(null);
    const [hoveredRating, setHoveredRating] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const [customName, setCustomName] = useState("");
    const [customRole, setCustomRole] = useState("");
    const [customText, setCustomText] = useState("");

    useEffect(() => {
        localStorage.setItem("gb_testimonials", JSON.stringify(reviewList));
    }, [reviewList]);

    // Client-side AI Generator Matrix (Zero network dependencies = 0% failure rate)
    const generateUniqueAIReview = (rating) => {
        setIsGenerating(true);
        setCustomText("");

        // Simulated processing response delay to mimic generative networks
        setTimeout(() => {
            const openers = {
                5: ["An absolute game-changer!", "Genius Bridge Technologies completely transformed my job search workflow.", "Outstanding recruitment experience from start to finish.", "The absolute gold standard for technical career advancement."],
                4: ["Very satisfied with my experience.", "Great placement support overall.", "A highly reliable platform for engineers looking for new opportunities.", "The tech matching process is solid and well-handled."],
                3: ["An okay experience overall.", "Decent recruitment services but has room for growth.", "The platform functions fine for career updates.", "Average placement experience."],
                1: ["Highly disappointed with the lack of progress.", "Extremely fragmented process from day one.", "Poor coordination and matching routines.", "Would not recommend based on my engagement."]
            };

            const midsections = {
                5: [" Badal provided incredible guidance and industry resources that accelerated everything.", " Every single staff member advocated heavily for my profile and technical skills.", " The entire timeline moved seamlessly from initial screening up to the final offer letter.", " Their strategic industry network landed me a premium role in record time."],
                4: [" Badal and the core tracking team were supportive throughout the interview stages.", " They set up multiple interviews with reputable tech startups within a few weeks.", " Clear communication structures kept me prepared for tough technical rounds.", " The staff handled my custom profile preferences with great care and attention."],
                3: [" Interview alignments took longer than initially promised by the team.", " Badal was helpful, though coordinating active follow-up calls required patience.", " The interface tools are useful, but scheduling synchronization felt volatile.", " Got a few matches, but they didn't completely fit my long-term trajectory."],
                1: [" Communication channels were entirely unresponsive despite submitting multiple preferences.", " Mismatched engineering job profiles were thrown my way without any technical review.", " Promised follow-ups and feedback loops never materialized.", " The recruitment workflow was frustratingly slow and disorganized."]
            };

            const closers = {
                5: [" I highly recommend them to anyone trying to take the next big step in their career!", " True partners in engineering success.", " Five stars well-deserved!", " I will definitely use their network again in the future."],
                4: [" Just a couple of minor scheduling delays away from a perfect score.", " Highly recommend their career branding support.", " Overall, a very positive transition experience.", " Solid tech partner for engineers."],
                3: [" It gets the core job done if you have a flexible timeline.", " An acceptable ecosystem for casual job searches.", " It's okay, but expect to do your own follow-up tracking.", " Average service packages."],
                1: [" Save your time and apply directly elsewhere.", " Highly frustrating experience.", " Major adjustments are required in their onboarding pipeline.", " Disappointed in how my application was handled."]
            };

            // Ratings 1 & 2 share the lower bracket logic
            const targetKey = rating <= 2 ? 1 : rating;

            // Pick randomized nodes to guarantee unique phrasing structures
            const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

            const compiledReview = `${pickRandom(openers[targetKey])}${pickRandom(midsections[targetKey])}${pickRandom(closers[targetKey])}`;

            setCustomText(compiledReview);
            setIsGenerating(false);
        }, 400);
    };

    const handleStarClick = (rating) => {
        setSelectedRating(rating);
        generateUniqueAIReview(rating);
    };

    const handleSubmitReview = (e) => {
        e.preventDefault();
        if (!customText.trim()) return;

        const newReview = {
            name: customName.trim() || "Anonymous User",
            role: customRole.trim() || "Job Seeker",
            rating: selectedRating,
            text: customText
        };

        setReviewList([newReview, ...reviewList]);
        setSelectedRating(null);
        setCustomName("");
        setCustomRole("");
        setCustomText("");
    };

    return (
        <div className="py-20 bg-brand-light text-center px-4 mx-auto">
            <h2 className="text-4xl font-bold mb-4">
                Client <span className="bg-brand-gradient bg-clip-text text-transparent">Testimonials</span>
            </h2>
            <p className="text-gray-500 mb-12 max-w-xl mx-auto">
                Real feedback from our users who achieved career success.
            </p>

            {/* AI GENERATOR & SUBMISSION FORM */}
            <div className="mb-16 bg-gradient-to-br from-slate-50 to-gray-100 border border-gray-200 rounded-3xl p-6 sm:p-8 max-w-3xl mx-auto shadow-sm">
                <div className="flex items-center justify-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-amber-500 fill-amber-500 animate-pulse" />
                    <h3 className="text-lg font-bold text-gray-800">Write a Review with Live Free AI</h3>
                </div>
                <p className="text-xs text-gray-400 mb-6">Select your rating score below to generate a completely non-repeating unique review draft.</p>

                {/* Star Picker Component */}
                <div className="flex justify-center gap-3 mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            disabled={isGenerating}
                            onClick={() => handleStarClick(star)}
                            onMouseEnter={() => setHoveredRating(star)}
                            onMouseLeave={() => setHoveredRating(null)}
                            className="transform hover:scale-125 transition-transform duration-150 focus:outline-none disabled:opacity-50"
                        >
                            <Star
                                className={`w-8 h-8 transition-colors ${star <= (hoveredRating || selectedRating || 0)
                                    ? "text-amber-500 fill-amber-500"
                                    : "text-gray-300"
                                    }`}
                            />
                        </button>
                    ))}
                </div>

                {/* Inline Dashboard Form */}
                {selectedRating && (
                    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-md text-left transition-all duration-300">
                        {isGenerating ? (
                            <div className="flex flex-col items-center justify-center gap-3 text-gray-400 text-sm py-8 text-center">
                                <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                                <p>Generating a unique {selectedRating}-star review matrix response...</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmitReview} className="space-y-4">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Your Name</label>
                                        <input
                                            type="text"
                                            value={customName}
                                            onChange={(e) => setCustomName(e.target.value)}
                                            placeholder="e.g. Jane Doe"
                                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary bg-slate-50/50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Job Role / Designation</label>
                                        <input
                                            type="text"
                                            value={customRole}
                                            onChange={(e) => setCustomRole(e.target.value)}
                                            placeholder="e.g. Frontend Developer"
                                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary bg-slate-50/50"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-xs font-semibold text-gray-500">Live AI Generated Draft (Editable)</label>
                                        <div className="flex items-center gap-2">
                                            {/* REFRESH/RE-GENERATE BUTTON */}
                                            <button
                                                type="button"
                                                onClick={() => generateUniqueAIReview(selectedRating)}
                                                disabled={isGenerating}
                                                className="text-[11px] text-brand-primary hover:bg-brand-primary/5 border border-brand-primary/20 px-2.5 py-1 rounded-lg flex items-center gap-1.5 transition-all font-medium active:scale-95 disabled:opacity-50"
                                            >
                                                <RefreshCw className="w-3 h-3" />
                                                Generate New Review
                                            </button>
                                            <span className="text-[10px] bg-emerald-100 text-emerald-800 font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                                                <Sparkles className="w-2.5 h-2.5" /> Engine Active
                                            </span>
                                        </div>
                                    </div>
                                    <textarea
                                        rows={3}
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        className="w-full text-sm p-3 border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary text-gray-600 italic resize-none leading-relaxed"
                                        required
                                    />
                                </div>

                                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedRating(null)}
                                        className="px-4 py-2 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs px-4 py-2 rounded-xl flex items-center gap-2 transition-all shadow-sm"
                                    >
                                        <Send className="w-3 h-3" /> Submit Review
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>

            {/* Testimonials Review Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6">
                {reviewList.map((review, index) => (
                    <div
                        key={index}
                        className="bg-white mx-7 border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between text-left"
                    >
                        <div>
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-primary text-white font-bold text-lg uppercase shadow-inner">
                                    {review.name ? review.name.charAt(0) : "A"}
                                </div>
                                <div>
                                    <h4 className="font-semibold text-base text-gray-800">{review.name}</h4>
                                    <p className="text-xs text-gray-400">{review.role}</p>
                                </div>
                            </div>

                            <div className="flex mb-3">
                                {[...Array(review.rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 text-brand-primary fill-brand-primary" />
                                ))}
                                {[...Array(5 - review.rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 text-gray-200" />
                                ))}
                            </div>

                            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                                {review.text}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}